import { isNil } from 'lodash';
import { createContext, useContext } from 'react';
import { API } from '@shared/types/api';
import { useListUsers } from '@shared/util/hooks';

type PersonRef = NonNullable<
  NonNullable<API.ListWorkflowEntitiesQuery['list']['refs']>['personRefs']
>[number];

/**
 * People a host already loaded with its rows (a subtable's person refs), so a field showing who
 * captured or signed something can skip fetching them.
 */
export const PersonRefsContext = createContext<PersonRef[] | null | undefined>(undefined);

/**
 * A person by id: from the host's person refs when it holds them, otherwise fetched, system and
 * deactivated users included: an automation can capture a location, and someone who signed
 * may since have left.
 */
export function useFieldUser(userId: number | null | undefined) {
  const personRefs = useContext(PersonRefsContext);
  const refUser = personRefs?.find(user => user.id === userId);
  const { users, loading } = useListUsers({
    input: {
      ids: [userId ?? -1],
      excludeSystemUsers: false,
      includeDeactivated: true,
      orderBys: [{ orderBy: 'firstName', direction: 'ASC' }],
    },
    skip: isNil(userId) || !isNil(refUser),
  });
  if (isNil(userId)) {
    return { user: undefined, loading: false };
  }
  return { user: refUser ?? users[0], loading };
}
